import type { FC, ReactNode } from "react";
import { useMemo } from "react";

import clsx from "clsx";
import { omit } from "ramda";

import type { LinkProps } from "react-router-dom";

import { Button, SkeletonBase, Text } from "shared/uikit";
import { ButtonSizesEnum, ButtonVariantsEnum, TextStylesEnum } from "shared/uikit/types";

import { uuidv4 } from "utils/serviceUtils/helpers";

import type { OptionComponentProps } from "../SelectBase";

export interface FieldOptionModel extends LinkProps {
	label: string | ReactNode;
	deleted?: boolean;
	error?: boolean;
}

interface FieldItemProps extends OptionComponentProps {
	active: number;
	handleSelected: () => void;
	loading?: boolean;
	variant: ButtonVariantsEnum | "UNDERLINED";
}

const FieldOption: FC<FieldItemProps> = ({ active, idx, option, handleSelected, loading, variant }) => {
	const id = useMemo(
		() => (typeof option.label === "string" ? option.label?.toLowerCase().replace(/\s/g, "-") : uuidv4()),
		[option]
	);

	if (loading) {
		return <SkeletonBase width={100} height={40} variant="rounded" />;
	}

	return (
		<Button
			link={{ ...omit(["label"], option) }}
			variant={
				active === idx
					? variant !== "UNDERLINED"
						? variant
						: ButtonVariantsEnum.OUTLINED
					: option?.error
						? ButtonVariantsEnum.DANGER
						: ButtonVariantsEnum.GHOST
			}
			secondary
			size={ButtonSizesEnum.XS}
			onClick={() => !(option as FieldOptionModel)?.to && handleSelected()}
			id={id}
		>
			{typeof option.label === "string" ? (
				<Text
					style={
						variant === "UNDERLINED"
							? TextStylesEnum.Default
							: active === idx
								? TextStylesEnum.Primary
								: TextStylesEnum.Hint
					}
					className={clsx(
						variant === "UNDERLINED" && active === idx && "underline underline-offset-4 decoration-primary-500"
					)}
				>
					{option.label}
				</Text>
			) : (
				<div className="relative">
					{option.label}
					<hr
						className={clsx(
							!(variant === "UNDERLINED" && active === idx) && "hidden",
							"w-full h-0.5 bg-primary-500 border-0"
						)}
					/>
				</div>
			)}
		</Button>
	);
};

export default FieldOption;
